import React from 'react';
import {
  Loading,
  RecordContextProvider,
  SingleFieldList,
  useGetOne,
  useRecordContext,
} from 'react-admin';
import { Chip, Grid } from '@mui/material';

import auditTrackDiffGenerator, { AuditTrack } from '../audit/auditDiffGenerator/auditTrackDiffGenerator';
import AuditExtraInfo from '../audit/AuditExtraInfo';
import NoResults from '../layout/NoResults';
import TextDiffField from '../utils/auditTracks/TextDiffField';
import ArrayDiffField from '../utils/auditTracks/ArrayDiffField';
import IconDiffField from '../utils/auditTracks/IconDiffField';
import { PermissionRole } from './type';
import { actionDescriptionMap } from './PermissionAccordion/permissionCategories';
import { Actions } from '../../constants/actions';

const PermissionDescriptionChip = () => {
  const record = useRecordContext<{ name: Actions }>();
  if (!record) return null;
  return <Chip label={actionDescriptionMap[record.name] ?? record.name} />;
};

type IPermissionRoleDiff = Omit<PermissionRole, 'id'>;

const getChangedPermissionNames = (
  oldPerms: PermissionRole['permissions'] = [],
  newPerms: PermissionRole['permissions'] = [],
): string[] => {
  const allNames = new Set([...oldPerms.map((p) => p.name), ...newPerms.map((p) => p.name)]);
  return [...allNames].filter((name) => {
    const oldPerm = oldPerms.find((p) => p.name === name);
    const newPerm = newPerms.find((p) => p.name === name);
    return JSON.stringify(oldPerm) !== JSON.stringify(newPerm);
  });
};

const toFilteredParams = (
  r: AuditTrack<PermissionRole>,
  changedNames: string[],
): AuditTrack<IPermissionRoleDiff> => ({
  ...r,
  documentAfter: r.documentAfter
    ? {
      ...r.documentAfter,
      permissions: r.documentAfter.permissions.filter((p) => changedNames.includes(p.name)),
    }
    : r.documentAfter,
});

const PermissionRoleAuditDetails = () => {
  const record = useRecordContext<AuditTrack<PermissionRole>>();
  const { data, isLoading } = useGetOne<AuditTrack<PermissionRole>>(
    `audit-tracks/${record?.id}/previous`,
    { id: '' },
    { enabled: !!record },
  );

  if (!record) return <NoResults />;

  if (!data) {
    if (isLoading) return <Loading />;
    if (record.extraInfo) return <AuditExtraInfo />;
    return <NoResults />;
  }

  const isElementCreated = data.id === record.id;

  if (!record.documentAfter || (!isElementCreated && !data.documentAfter)) {
    if (record.extraInfo) return <AuditExtraInfo />;
    return <NoResults />;
  }

  const changedPermissionNames = getChangedPermissionNames(
    !isElementCreated ? data.documentAfter?.permissions : [],
    record.documentAfter?.permissions,
  );

  const auditTracksDiff = auditTrackDiffGenerator<IPermissionRoleDiff>({
    current: toFilteredParams(record, changedPermissionNames),
    previous: !isElementCreated ? toFilteredParams(data, changedPermissionNames) : undefined,
  });

  const { resourceData } = auditTracksDiff;

  const hasFieldChanges = {
    name: resourceData.name?.hasChanged,
    description: resourceData.description?.hasChanged,
    baseRole: resourceData.baseRole?.hasChanged,
    iconName: resourceData.iconName?.hasChanged,
    permissions: resourceData.permissions?.hasChanged,
  };

  const hasAnyChanges = Object.values(hasFieldChanges).some((field) => field);

  return (
    <Grid container spacing={6}>
      {!hasAnyChanges && <NoResults text="Only system changes detected" />}
      {hasAnyChanges && (
        <RecordContextProvider value={resourceData}>
          {hasFieldChanges.name && <TextDiffField source="name" label="Name" />}
          {hasFieldChanges.description && <TextDiffField source="description" label="Description" />}
          {hasFieldChanges.baseRole && <TextDiffField source="baseRole" label="Base role" />}
          {hasFieldChanges.iconName && (
            <IconDiffField
              label="Icon"
              oldIconName={!isElementCreated ? data.documentAfter?.iconName : undefined}
              newIconName={record.documentAfter?.iconName}
            />
          )}
          {hasFieldChanges.permissions && (
            <ArrayDiffField source="permissions" label="Permissions">
              <SingleFieldList linkType={false}>
                <PermissionDescriptionChip />
              </SingleFieldList>
            </ArrayDiffField>
          )}
        </RecordContextProvider>
      )}
      {record.extraInfo && <AuditExtraInfo />}
    </Grid>
  );
};

export default PermissionRoleAuditDetails;
